import React, { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import clsx from "clsx";

import AppBox from "@/components/AppBox";
import AppIcon from "@/components/AppIcon";
import AppText from "@/components/AppText";
import DropdownMenu from "@/components/DropdownMenu";
import AppModal from "@/components/AppModal";
import AppTooltip from "@/components/AppTooltip";
import SendSupportModal from "@/components/Modals/SendSupportModal";

import { userLogout, adminLogout } from "@/store/auth";

const cookies = new Cookies();

const mobilePagesUser = [
  { name: "Dashboard", url: "/" },
  { name: "Configuration", url: "/configuration" },
  { name: "Billing", url: "/billing" },
  { name: "Reports", url: "/reports" },
  { name: "Agency", url: "/agency" },
];

const mobilePagesAdmin = [
  { name: "Live users", url: "/panel" },
  { name: "Canceled users", url: "/panel/canceled" },
  { name: "Plans", url: "/panel/plans" },
  { name: "System Alerts", url: "/panel/alerts" },
];

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { me } = useAppSelector((state) => state.auth);

  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const mobilePages = me?.isAdmin ? mobilePagesAdmin : mobilePagesUser;
  
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    
    const handleClickOutside = (event: MouseEvent) => {
      const menuButton = document.querySelector('[data-menu-button]');
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !menuButton?.contains(event.target as Node)
      ) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMobileMenu]);

  const handleNavigateTo = (page: string) => {
    navigate(`/${page}`);
  };

  const handleLogout = async () => {
    // return
    const tokenAgency = cookies.get("gls-agency-token");
    dispatch(me?.isAdmin ? adminLogout() : userLogout());
    if (tokenAgency) {
      navigate("/agency");
      return;
    }
    const token = cookies.get("admin-gls-token");
    if (token) {
      navigate("/panel");
      return;
    }
    navigate("/");
  };

  const userMenuItems = [
    { label: "Billing", onClick: () => handleNavigateTo("billing") },
    { label: "Logout", onClick: handleLogout },
  ];

  const handleClickMobileMenuItem = (url: string) => {
    setShowMobileMenu(false);
    if (url === "/support") {
      handleToggleSupportModal();
      return;
    }
    navigate(url);
  };

  const handleToggleMobileMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowMobileMenu((prev) => !prev);
  };

  const handleToggleSupportModal = () => {
    setIsOpen(!isOpen)
  };

  const handleOpenBookCallLink = () => {
    window.open("https://calendly.com/lsarespond", "_blank");
  };

  return (
    <AppBox fullWidth className="fixed top-0 shadow-md z-[60]">
      <AppBox fullWidth justify="between" align="center" className="bg-white px-4 py-2 relative z-30">
        {/* Logo */}
        <AppBox align="center">
          <img
            src='/images/logo.png'
            alt="Website Logo"
            className={clsx("w-[220px]", me?.agency?.id && !me?.is_agency_admin && "hidden")}
          />
          {me?.agency?.id && !me?.is_agency_admin && (
            <AppText variant="h3">LSA Auto-Responder</AppText>
          )}
        </AppBox>

        {/* Mobile Menu */}
        <AppBox className="md:hidden">
          <button
            onClick={handleToggleMobileMenu}
            data-menu-button
            className={clsx(
              "p-2 rounded transition",
              showMobileMenu ? "bg-gray-200" : "bg-gray-100 hover:bg-gray-200"
            )}
            aria-expanded={showMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <AppIcon name="menu" />
          </button>
        </AppBox>

        <AppBox align="center" className="hidden md:flex" gap={8}>
          {/* Support Button */}
          {!me?.isAdmin && (
            <AppBox gap={8} className="hidden md:flex">
              <button
                onClick={handleOpenBookCallLink}
                className="text-primary font-medium hover:text-green-600 transition"
              >
                BOOK A CALL
              </button>
              
              <button
                onClick={handleToggleSupportModal}
                className="text-primary font-medium hover:text-green-600 transition"
              >
                SUPPORT
              </button>
            </AppBox>
          )}

          {/* User Menu */}
          <DropdownMenu
            trigger={
              <AppBox className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition">
                <AppTooltip 
                  title={
                    me?.agency?.id && !me?.is_agency_admin
                      ? `${me?.company ?? me?.name} (${me?.agency?.name} Agency)`
                      : `${me?.name} ${me?.signup_email ? `(${me?.signup_email})` : ''}`
                  }
                  enterDelay={200}
                  placement="bottom"
                  PopperProps={{
                    modifiers: [{
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    }],
                    popperOptions: {
                      strategy: 'fixed'
                    }
                  }}
                >
                  <AppBox className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white">
                    {me?.name?.charAt(0).toUpperCase()}
                  </AppBox>
                </AppTooltip>
              </AppBox>
            }
            items={userMenuItems}
            className="z-[700]"
          />
        </AppBox>
      </AppBox>

      <AppBox ref={mobileMenuRef} column style={{zIndex: 10}} className={clsx(
        "absolute z-10 inset-x-0 bg-white shadow-lg border-t-4 border-t-gray-200 transition-transform duration-300 ease-in-out",
        {
          "top-[52px] translate-y-0": showMobileMenu,
          "top-[52px] -translate-y-full": !showMobileMenu
        }
      )}>
        {/* Regular Menu Items */}
        {mobilePages.map((page) => {
          if (
            (page.name === "Agency" && !me?.is_agency_admin) ||
            (page.name === "Billing" && me?.agency?.id && !me?.is_agency_admin)
          ) {
            return null;
          }

          return (
            <button
              key={page.name}
              onClick={() => handleClickMobileMenuItem(page.url)}
              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              {page.name}
            </button>
          )
        })}

        {/* Support Links for Mobile */}
        {!me?.isAdmin && (
          <AppBox column className="border-b border-t border-gray-200">
            <button
              onClick={() => {
                setShowMobileMenu(false);
                handleOpenBookCallLink();
              }}
              className="block w-full text-left px-4 py-2 text-primary font-medium hover:bg-gray-100"
            >
              BOOK A CALL
            </button>
            <button
              onClick={() => handleClickMobileMenuItem("/support")}
              className="block w-full text-left px-4 py-2 text-primary font-medium hover:bg-gray-100"
            >
              SUPPORT
            </button>
          </AppBox>
        )}

        {/* Logout Section */}
        <AppBox className="border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
          >
            Logout
          </button>
        </AppBox>
      </AppBox>

      {/* Support Modal */}
      <AppModal isOpen={isOpen} title="Send Support Request" onClose={handleToggleSupportModal}>
        <SendSupportModal onClose={handleToggleSupportModal} />
      </AppModal>
    </AppBox>
  );
};

export default AppHeader;